import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { 
  DollarSign, 
  Plus, 
  X, 
  Search, 
  Filter,
  CheckCircle,
  AlertCircle,
  Calendar,
  User,
  Wallet,
  TrendingDown,
  Edit,
  Trash2,
  Eye,
  ChevronDown,
  ArrowDownCircle
} from 'lucide-react'
import './AdvancePayment.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const statusColors = {
  'Pending': '#ff9800',
  'Approved': '#2196f3',
  'Issued': '#4caf50',
  'Rejected': '#f44336',
  'Partially Paid': '#9c27b0'
}

export default function AdvancePayment() {
  const [records, setRecords] = useState([])
  const [personnel, setPersonnel] = useState([])
  const [guards, setGuards] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })

  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    employeeType: 'Personnel',
    basicSalary: '',
    requestedAmount: '',
    amountIssued: '',
    requestDate: '',
    issueDate: '',
    status: 'Pending',
    reason: '',
    notes: ''
  })

  useEffect(() => {
    fetchRecords()
    fetchPersonnel()
    fetchGuards()
  }, [])

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/advance-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setRecords(res.data.data || [])
    } catch (err) {
      showMessage('error', 'Failed to load records')
    } finally {
      setLoading(false)
    }
  }

  const fetchPersonnel = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/personnel`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPersonnel(res.data.data || [])
    } catch (err) {
      console.log('Failed to load personnel')
    }
  }

  const fetchGuards = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/guards`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGuards(res.data.data || [])
    } catch (err) {
      console.log('Failed to load guards')
    }
  }

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 5000)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token')
      const data = {
        ...formData,
        basicSalary: parseFloat(formData.basicSalary) || 0,
        requestedAmount: parseFloat(formData.requestedAmount) || 0,
        amountIssued: parseFloat(formData.amountIssued) || 0,
        remainingAmount: Math.max(0, (parseFloat(formData.basicSalary) || 0) - (parseFloat(formData.amountIssued) || 0))
      }

      if (selectedRecord) {
        await axios.put(`${API_URL}/advance-payments/${selectedRecord._id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showMessage('success', 'Advance payment updated')
      } else {
        await axios.post(`${API_URL}/advance-payments`, data, {
          headers: { Authorization: `Bearer ${token}` }
        })
        showMessage('success', 'Advance payment requested')
      }
      setShowModal(false)
      resetForm()
      fetchRecords()
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Operation failed')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/advance-payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Record deleted')
      fetchRecords()
    } catch (err) {
      showMessage('error', 'Delete failed')
    }
  }

  const openModal = (record = null) => {
    if (record) {
      setSelectedRecord(record)
      setFormData({
        employeeId: record.employeeId || '',
        employeeName: record.employeeName || '',
        employeeType: record.employeeType || 'Personnel',
        basicSalary: record.basicSalary || '',
        requestedAmount: record.requestedAmount || '',
        amountIssued: record.amountIssued || '',
        requestDate: record.requestDate ? record.requestDate.split('T')[0] : '',
        issueDate: record.issueDate ? record.issueDate.split('T')[0] : '',
        status: record.status || 'Pending',
        reason: record.reason || '',
        notes: record.notes || ''
      })
    } else {
      setSelectedRecord(null)
      resetForm()
    }
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      employeeId: '',
      employeeName: '',
      employeeType: 'Personnel',
      basicSalary: '',
      requestedAmount: '',
      amountIssued: '',
      requestDate: '',
      issueDate: '',
      status: 'Pending',
      reason: '',
      notes: ''
    })
  }

  const handleEmployeeChange = (e) => {
    const selectedId = e.target.value
    const type = formData.employeeType
    const list = type === 'Personnel' ? personnel : guards
    const selected = list.find(p => p._id === selectedId)
    
    setFormData({
      ...formData,
      employeeId: selectedId,
      employeeName: selected ? `${selected.firstName} ${selected.lastName}` : '',
      basicSalary: selected?.basicSalary || selected?.salary || ''
    })
  }

  const filteredRecords = records.filter(r => {
    const search = searchTerm.toLowerCase()
    return (
      (r.employeeName || '').toLowerCase().includes(search) ||
      (r.status || '').toLowerCase().includes(search)
    ) && (statusFilter ? r.status === statusFilter : true)
  })

  const totalRequested = records.reduce((sum, r) => sum + (r.requestedAmount || 0), 0)
  const totalIssued = records.reduce((sum, r) => sum + (r.amountIssued || 0), 0)
  const pendingCount = records.filter(r => r.status === 'Pending').length

  return (
    <div className="advance-payment-module">
      {/* Header Stats */}
      <div className="ap-stats-grid">
        <div className="ap-stat-card primary">
          <div className="ap-stat-icon"><Wallet size={24} /></div>
          <div className="ap-stat-value">KES {totalRequested.toLocaleString()}</div>
          <div className="ap-stat-label">Total Requested</div>
        </div>
        <div className="ap-stat-card success">
          <div className="ap-stat-icon"><CheckCircle size={24} /></div>
          <div className="ap-stat-value">KES {totalIssued.toLocaleString()}</div>
          <div className="ap-stat-label">Total Issued</div>
        </div>
        <div className="ap-stat-card warning">
          <div className="ap-stat-icon"><AlertCircle size={24} /></div>
          <div className="ap-stat-value">{pendingCount}</div>
          <div className="ap-stat-label">Pending Requests</div>
        </div>
        <div className="ap-stat-card info">
          <div className="ap-stat-icon"><TrendingDown size={24} /></div>
          <div className="ap-stat-value">{records.length}</div>
          <div className="ap-stat-label">Total Records</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ap-toolbar">
        <div className="ap-search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by name or status..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="ap-filter-box">
          <Filter size={18} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Issued">Issued</option>
            <option value="Rejected">Rejected</option>
            <option value="Partially Paid">Partially Paid</option>
          </select>
        </div>
        <button className="ap-btn-primary" onClick={() => openModal()}>
          <Plus size={18} /> Request Advance
        </button>
      </div>

      {message.text && (
        <div className={`ap-alert ap-alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Table */}
      <div className="ap-table-container">
        <table className="ap-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Type</th>
              <th>Basic Salary</th>
              <th>Requested</th>
              <th>Issued</th>
              <th>Remaining</th>
              <th>Request Date</th>
              <th>Issue Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" className="ap-loading">Loading...</td></tr>
            ) : filteredRecords.length === 0 ? (
              <tr><td colSpan="10" className="ap-empty">No advance payment records</td></tr>
            ) : (
              filteredRecords.map(record => (
                <tr key={record._id}>
                  <td>
                    <div className="ap-employee">
                      <User size={16} />
                      <span>{record.employeeName}</span>
                    </div>
                  </td>
                  <td><span className="ap-badge">{record.employeeType}</span></td>
                  <td>KES {(record.basicSalary || 0).toLocaleString()}</td>
                  <td className="ap-amount-requested">KES {(record.requestedAmount || 0).toLocaleString()}</td>
                  <td className="ap-amount-issued">KES {(record.amountIssued || 0).toLocaleString()}</td>
                  <td className="ap-amount-remaining">KES {(record.remainingAmount || 0).toLocaleString()}</td>
                  <td>{record.requestDate ? new Date(record.requestDate).toLocaleDateString() : '-'}</td>
                  <td>{record.issueDate ? new Date(record.issueDate).toLocaleDateString() : '-'}</td>
                  <td>
                    <span className="ap-status-badge" style={{ backgroundColor: statusColors[record.status] + '20', color: statusColors[record.status] }}>
                      {record.status}
                    </span>
                  </td>
                  <td>
                    <button className="ap-btn-icon" onClick={() => openModal(record)} title="Edit"><Edit size={14} /></button>
                    <button className="ap-btn-icon ap-btn-danger" onClick={() => handleDelete(record._id)} title="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="ap-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="ap-modal-content">
            <div className="ap-modal-header">
              <h3>{selectedRecord ? 'Edit' : 'Request'} Advance Payment</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="ap-form">
              <div className="ap-form-row">
                <div className="ap-form-group">
                  <label>Employee Type *</label>
                  <select 
                    value={formData.employeeType} 
                    onChange={(e) => setFormData({...formData, employeeType: e.target.value, employeeId: '', employeeName: '', basicSalary: ''})}
                    required
                  >
                    <option value="Personnel">Personnel</option>
                    <option value="Guard">Guard</option>
                  </select>
                </div>
                <div className="ap-form-group">
                  <label>Select Employee *</label>
                  <select 
                    value={formData.employeeId} 
                    onChange={handleEmployeeChange}
                    required
                  >
                    <option value="">Select {formData.employeeType}</option>
                    {(formData.employeeType === 'Personnel' ? personnel : guards).map(p => (
                      <option key={p._id} value={p._id}>
                        {p.firstName} {p.lastName} {p.guardId ? `(${p.guardId})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ap-form-row">
                <div className="ap-form-group">
                  <label>Basic Salary (KES)</label>
                  <input 
                    type="number" 
                    value={formData.basicSalary}
                    onChange={(e) => setFormData({...formData, basicSalary: e.target.value})}
                    placeholder="Auto-filled from employee"
                    readOnly
                  />
                </div>
                <div className="ap-form-group">
                  <label>Requested Amount (KES) *</label>
                  <input 
                    type="number" 
                    value={formData.requestedAmount}
                    onChange={(e) => setFormData({...formData, requestedAmount: e.target.value})}
                    required
                    min="0"
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="ap-form-row">
                <div className="ap-form-group">
                  <label>Amount Issued (KES)</label>
                  <input 
                    type="number" 
                    value={formData.amountIssued}
                    onChange={(e) => setFormData({...formData, amountIssued: e.target.value})}
                    min="0"
                    placeholder="Enter issued amount"
                  />
                </div>
                <div className="ap-form-group">
                  <label>Request Date</label>
                  <input 
                    type="date" 
                    value={formData.requestDate}
                    onChange={(e) => setFormData({...formData, requestDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="ap-form-row">
                <div className="ap-form-group">
                  <label>Issue Date</label>
                  <input 
                    type="date" 
                    value={formData.issueDate}
                    onChange={(e) => setFormData({...formData, issueDate: e.target.value})}
                  />
                </div>
                <div className="ap-form-group">
                  <label>Status</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Issued">Issued</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Partially Paid">Partially Paid</option>
                  </select>
                </div>
              </div>

              <div className="ap-form-row">
                <div className="ap-form-group ap-form-full">
                  <label>Reason for Advance</label>
                  <textarea 
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    rows="3"
                    placeholder="Enter reason..."
                  />
                </div>
              </div>

              <div className="ap-form-row">
                <div className="ap-form-group ap-form-full">
                  <label>Notes</label>
                  <textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="2"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              {/* Real-time Calculation Display */}
              <div className="ap-calculation-card">
                <div className="ap-calc-row">
                  <span>Basic Salary:</span>
                  <strong>KES {parseFloat(formData.basicSalary || 0).toLocaleString()}</strong>
                </div>
                <div className="ap-calc-row">
                  <span>Requested:</span>
                  <strong className="ap-calc-requested">KES {parseFloat(formData.requestedAmount || 0).toLocaleString()}</strong>
                </div>
                <div className="ap-calc-row">
                  <span>Issued:</span>
                  <strong className="ap-calc-issued">KES {parseFloat(formData.amountIssued || 0).toLocaleString()}</strong>
                </div>
                <div className="ap-calc-divider" />
                <div className="ap-calc-row ap-calc-total">
                  <span>Remaining Balance:</span>
                  <strong>KES {Math.max(0, (parseFloat(formData.basicSalary || 0) - parseFloat(formData.amountIssued || 0))).toLocaleString()}</strong>
                </div>
              </div>

              <div className="ap-modal-actions">
                <button type="button" className="ap-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="ap-btn-primary">
                  <ArrowDownCircle size={16} /> {selectedRecord ? 'Update' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
